import React from 'react';
import { ShieldCheck, Shield } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

/**
 * Badge "Vérifié" pour les utilisateurs ayant complété la vérification d'identité Stripe
 * @param {boolean} verified - Si l'utilisateur est vérifié
 * @param {string} size - Taille du badge: 'sm', 'md', 'lg'
 * @param {boolean} showLabel - Afficher le texte "Vérifié"
 */
export const VerifiedBadge = ({ verified = false, size = 'sm', showLabel = false, className = '' }) => {
  if (!verified) return null;

  const sizeClasses = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6',
  };

  const textSizes = {
    sm: 'text-xs',
    md: 'text-sm',
    lg: 'text-base',
  };

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <span 
            className={`inline-flex items-center gap-1 text-blue-600 ${className}`}
            data-testid="verified-badge"
          >
            <ShieldCheck className={`${sizeClasses[size]} fill-blue-100`} />
            {showLabel && (
              <span className={`font-medium ${textSizes[size]}`}>Vérifié</span>
            )}
          </span>
        </TooltipTrigger>
        <TooltipContent>
          <p className="text-sm">Identité vérifiée par Stripe</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

/**
 * Badge complet avec fond coloré pour les profils
 */
export const VerifiedProfileBadge = ({ verified = false, className = '' }) => {
  if (!verified) return null;

  return (
    <span 
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 text-sm font-medium rounded-full border border-blue-200 ${className}`}
      data-testid="verified-profile-badge"
    >
      <ShieldCheck className="w-4 h-4 fill-blue-100" />
      Identité vérifiée
    </span>
  );
};

/**
 * Indicateur de statut de vérification (pour la page profil)
 */
export const VerificationStatus = ({ status, className = '' }) => {
  const statusConfig = {
    not_started: {
      icon: Shield,
      label: 'Non vérifié',
      color: 'text-slate-500 bg-slate-50 border-slate-200',
      iconColor: 'text-slate-400',
    },
    pending: {
      icon: Shield,
      label: 'En cours de vérification',
      color: 'text-amber-700 bg-amber-50 border-amber-200',
      iconColor: 'text-amber-500',
    },
    verified: {
      icon: ShieldCheck,
      label: 'Identité vérifiée',
      color: 'text-blue-700 bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600',
    },
    failed: {
      icon: Shield,
      label: 'Vérification échouée',
      color: 'text-red-700 bg-red-50 border-red-200',
      iconColor: 'text-red-500',
    },
  };

  const config = statusConfig[status] || statusConfig.not_started;
  const Icon = config.icon;

  return (
    <span 
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-sm font-medium rounded-full border ${config.color} ${className}`}
      data-testid="verification-status"
    >
      <Icon className={`w-4 h-4 ${config.iconColor}`} />
      {config.label}
    </span>
  );
};

export default VerifiedBadge;
